import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Terminal, Zap, ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CommandMode = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [command, setCommand] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const exampleCommands = t('commandMode.examples', { returnObjects: true });

  const handleExecute = async (customCommand = null) => {
    const currentCommand = customCommand || command;
    if (!currentCommand.trim()) return;

    setLoading(true);
    setResult(null);

    try {
      const response = await axios.post(`${API}/ai/command`, {
        command: currentCommand,
        language: i18n.resolvedLanguage?.startsWith('en') ? 'en' : 'pl'
      }, { withCredentials: true });

      setResult({
        command: currentCommand,
        output: response.data.result,
        timestamp: new Date()
      });
      setCommand('');
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Command Error:', error);
      }
      setResult({
        command: currentCommand,
        output: t('commandMode.errorGeneric'),
        timestamp: new Date()
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleExecute();
    }
  };

  return (
    <div className="hussar-app hussar-command-mode">
      {/* Header */}
      <header className="hussar-app-header">
        <div className="hussar-app-header-content">
          <Button 
            variant="ghost" 
            className="hussar-btn-back"
            onClick={() => navigate('/')}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="hussar-logo-full">
            <img src="/hussarai-logo-full.png" alt="HussarAi" className="hussar-logo-full-img-small" />
          </div>
          <div className="hussar-command-badge">
            <Terminal className="w-4 h-4" />
            {t('commandMode.badge')}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="hussar-app-main">
        <div className="hussar-app-container">
          {loading && (
            <div className="hussar-command-processing">
              <Loader2 className="w-12 h-12 animate-spin hussar-loader" />
              <p className="hussar-processing-text">{t('commandMode.processing')}</p>
            </div>
          )}
          
          {!loading && !result && (
            <>
              {/* Command Mode Header */}
              <div className="hussar-command-header">
                <div className="hussar-command-icon">
                  <Zap className="w-12 h-12" />
                </div>
                <h1 className="hussar-command-title">{t('commandMode.title')}</h1>
                <p className="hussar-command-subtitle">
                  {t('commandMode.subtitle')}
                </p>
              </div>

              {/* Example Commands */}
              <div className="hussar-command-examples">
                <h3 className="hussar-examples-title">{t('commandMode.examplesTitle')}</h3>
                <div className="hussar-examples-grid">
                  {exampleCommands.map((example) => (
                    <Card 
                      key={example}
                      className="hussar-example-card"
                      onClick={() => handleExecute(example)}
                    >
                      <Terminal className="w-5 h-5 hussar-example-icon" />
                      <span>{example}</span>
                    </Card>
                  ))}
                </div>
              </div>
            </>
          )}
          
          {!loading && result && (
            <div className="hussar-command-result">
              <div className="hussar-result-header">
                <h3 className="hussar-result-title">{t('commandMode.resultTitle')}</h3>
                <p className="hussar-result-command">{t('commandMode.resultCommand', { command: result.command })}</p>
              </div>
              <Card className="hussar-result-card">
                <div className="hussar-result-content">
                  {result.output}
                </div>
              </Card>
              <div className="hussar-result-actions">
                <Button 
                  className="hussar-btn-secondary"
                  onClick={() => setResult(null)}
                >
                  {t('commandMode.newCommand')}
                </Button>
                <Button 
                  className="hussar-btn-primary"
                  onClick={() => navigator.clipboard.writeText(result.output)}
                >
                  {t('commandMode.copyResult')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Command Input */}
      <div className="hussar-input-section">
        <div className="hussar-input-container">
          <Card className="hussar-command-input-card">
            <Terminal className="hussar-command-terminal-icon" />
            <Input
              value={command}
              onChange={(e) => setCommand(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={t('commandMode.placeholder')}
              className="hussar-command-input"
              disabled={loading}
            />
            <Button 
              onClick={() => handleExecute()}
              disabled={!command.trim() || loading}
              className="hussar-btn-primary hussar-btn-execute"
            >
              <span>{t('commandMode.execute')}</span>
              <Zap className="w-4 h-4" />
            </Button>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CommandMode;
